import { useRef, useState, useEffect, useCallback } from 'react'
import { FileText, Upload, Loader2, AlertTriangle } from 'lucide-react'
import { uploadSupplierDocument } from '@/features/proveedores/api/supplierProfile.service'
import {
  getMisDocumentosFinancieros,
  estadoDocumento,
  errorMessage,
  fmtFecha,
  aFechaInput,
  ESTADO_META,
  type FinancialDocsState,
  type FinancialDocEstado,
} from '@/features/proveedores/api/supplierFinancialDocs.service'
import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

// Sección "Documentos solicitados" del portal del proveedor
// (C-NEW-02 / SP-210), conectada al back de SP-211:
// GET  /suppliers/profile/financial-documents  → solicitud activa + estado
// POST /suppliers/profile/documents            → subir (endpoint genérico),
//                                                con la fecha de vencimiento
//
// Si Contraloría no ha solicitado nada, la sección no se muestra.

const DocumentosSolicitadosSection = () => {
  const [state, setState] = useState<FinancialDocsState | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Archivo elegido por tipo de documento, antes de subirlo.
  const [elegidos, setElegidos] = useState<Record<string, File>>({})
  // Fecha de vencimiento capturada por el proveedor, por tipo de documento.
  const [vencimientos, setVencimientos] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [uploadingId, setUploadingId] = useState<string | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      setState(await getMisDocumentosFinancieros())
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setLoadError(errorMessage(err, 'No se pudieron cargar los documentos solicitados.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Se puede subir mientras esté pendiente o lo hayan rechazado.
  const isEditable = (estado: FinancialDocEstado) =>
    estado === 'pendiente' || estado === 'rechazado'

  const handleFile = (documentTypeId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      setErrors((prev) => ({ ...prev, [documentTypeId]: 'Solo se permiten archivos PDF.' }))
      e.target.value = ''
      return
    }
    setErrors((prev) => ({ ...prev, [documentTypeId]: '' }))
    setElegidos((prev) => ({ ...prev, [documentTypeId]: file }))
  }

  const handleSubir = async (
    documentTypeId: string,
    vencimiento: string,
    requiereVigencia: boolean
  ) => {
    const file = elegidos[documentTypeId]
    if (!file) return
    if (requiereVigencia && !vencimiento) {
      setErrors((prev) => ({
        ...prev,
        [documentTypeId]: 'Este documento tiene vigencia: captura la fecha de vencimiento.',
      }))
      return
    }
    setUploadingId(documentTypeId)
    setErrors((prev) => ({ ...prev, [documentTypeId]: '' }))
    try {
      // Sin fecha se manda sin ella y el back la deja en null.
      await uploadSupplierDocument(documentTypeId, file, vencimiento || undefined)
      setElegidos((prev) => {
        const resto = { ...prev }
        delete resto[documentTypeId]
        return resto
      })
      setVencimientos((prev) => {
        const resto = { ...prev }
        delete resto[documentTypeId]
        return resto
      })
      // Se relee para que el estatus lo dicte el back y no una suposición local.
      await load()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setErrors((prev) => ({
        ...prev,
        [documentTypeId]: errorMessage(err, 'No se pudo subir el documento.'),
      }))
    } finally {
      setUploadingId(null)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-8 mb-6 flex items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Cargando documentos solicitados…
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="flex items-start gap-3 px-4 py-3 mb-6 bg-red-50 border border-red-200 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-red-800">
          <p>{loadError}</p>
          <button onClick={load} className="mt-1 font-medium underline hover:no-underline">
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  // Sin solicitud activa no hay nada que mostrarle al proveedor.
  const documentos = state?.documents ?? []
  if (!state?.requestedAt || documentos.length === 0) return null

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB] mb-6">
      <div className="px-5 py-4 border-b border-[#E5E7EB]">
        <h3 className="text-base font-semibold text-gray-900">Documentos solicitados</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Contraloría te solicitó {documentos.length === 1 ? 'este documento financiero' : `estos ${documentos.length} documentos financieros`}. Súbelos en PDF.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#E5E7EB]">
          <thead className="bg-[#FAF9FF]">
            <tr>
              <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128]">Documento</th>
              <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128]">Archivo</th>
              <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128] whitespace-nowrap">Fecha de vencimiento</th>
              <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128]">Estatus</th>
              <th className="px-5 py-3 text-left text-sm font-medium text-[#1B2128]">Acción</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E1E1E1]">
            {documentos.map((req, i) => {
              const tipoId = req.documentType.id
              const doc = req.latestDocument
              const estado = estadoDocumento(doc)
              const meta = ESTADO_META[estado]
              const editable = isEditable(estado)
              const elegido = elegidos[tipoId]
              const subiendo = uploadingId === tipoId
              // Hoy los 4 financieros vienen sin vigencia, así que la fecha es
              // opcional. Si algún día se les configura, se vuelve obligatoria.
              const requiereVigencia = req.documentType.validityMonths != null
              // Lo capturado gana; si no se ha tocado, se parte de lo que ya
              // tenga el documento (útil al resubir uno rechazado).
              const vencimiento = vencimientos[tipoId] ?? aFechaInput(doc?.validUntil)
              return (
                <tr key={tipoId} className="hover:bg-[#F4F7FB] transition-colors align-top">
                  <td className="px-5 py-4 text-sm text-gray-900">
                    {i + 1}. {req.documentType.name}
                    {estado === 'rechazado' && doc?.reviewComment && (
                      <p className="text-xs text-red-600 mt-1">
                        Motivo del rechazo: {doc.reviewComment}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm">
                    {editable ? (
                      <>
                        <button
                          onClick={() => fileRefs.current[tipoId]?.click()}
                          disabled={subiendo}
                          className="inline-flex items-center gap-1.5 text-[#315FB9] hover:underline font-medium disabled:opacity-50"
                        >
                          <Upload className="w-4 h-4" />
                          {elegido?.name || doc?.fileName || 'Seleccionar PDF'}
                        </button>
                        <input
                          ref={(el) => { fileRefs.current[tipoId] = el }}
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          onChange={handleFile(tipoId)}
                        />
                        {errors[tipoId] && (
                          <p className="text-xs text-red-600 mt-1">{errors[tipoId]}</p>
                        )}
                      </>
                    ) : doc?.fileName ? (
                      <span className="inline-flex items-center gap-1.5 text-gray-700">
                        <FileText className="w-4 h-4 text-gray-400" />
                        {doc.fileName}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm whitespace-nowrap">
                    {editable ? (
                      <input
                        type="date"
                        value={vencimiento}
                        disabled={subiendo}
                        onChange={(e) =>
                          setVencimientos((prev) => ({ ...prev, [tipoId]: e.target.value }))
                        }
                        className="px-3 py-1.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                      />
                    ) : (
                      <span className="text-gray-700">{fmtFecha(doc?.validUntil)}</span>
                    )}
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    <Badge color={meta.color}>{meta.label}</Badge>
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap">
                    {editable ? (
                      <button
                        onClick={() => handleSubir(tipoId, vencimiento, requiereVigencia)}
                        disabled={!elegido || subiendo}
                        className={estiloDeBoton('primario')}
                      >
                        {subiendo ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Subiendo…
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Subir
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DocumentosSolicitadosSection
